import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Search, Package, AlertTriangle, RefreshCw,
  ArrowRight, Store, ChevronDown,
} from "lucide-react";
import { listStock } from "../../api/inventory";
import { listBranches } from "../../api/branches";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Card, Input, Select, EmptyState, Skeleton } from "../../components/ui";
import { unwrapList, errorMessage } from "../../lib/records";

// API max limit is 100; fetch all pages sequentially so the accordion has
// every item without exceeding the server's validation limit.
const API_LIMIT = 100;

/* ── Quantity display ─────────────────────────────────────────────────────── */
function QuantityPill({ qty, unit }) {
  if (qty == null) return <span className="text-sm text-gray-400">—</span>;
  const zero = qty === 0;
  const low  = !zero && qty <= 10;
  return (
    <div className="flex items-center gap-1.5">
      <span
        className={`text-lg font-bold tabular-nums leading-none ${
          zero ? "text-red-600" : low ? "text-amber-600" : "text-emerald-700"
        }`}
      >
        {qty}
      </span>
      {unit && <span className="text-xs text-gray-400">{unit}</span>}
      {zero && (
        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700">
          Out
        </span>
      )}
      {low && (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
          Low
        </span>
      )}
    </div>
  );
}

/* ── Smooth accordion ─────────────────────────────────────────────────────── */
function AccordionPanel({ open, children }) {
  const ref = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!ref.current) return;
    // Always measure the real scrollHeight so the transition is accurate
    setHeight(open ? ref.current.scrollHeight : 0);
  }, [open, children]);

  return (
    <div
      style={{
        height: `${height}px`,
        overflow: "hidden",
        transition: "height 260ms cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div ref={ref}>{children}</div>
    </div>
  );
}

/* ── Category accordion row ──────────────────────────────────────────────── */
function CategoryRow({ name, items, open, onToggle, isChain }) {
  const outOfStock = items.filter((r) => (r.quantity ?? 0) === 0).length;
  const lowStock   = items.filter((r) => (r.quantity ?? 0) > 0 && (r.quantity ?? 0) <= 10).length;
  const hasAlert   = outOfStock > 0 || lowStock > 0;

  return (
    <div className="border-b border-emerald-100/60 last:border-0">
      {/* ── header ── */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-4 sm:px-5 py-3.5 text-left transition-colors hover:bg-emerald-50/50 focus:outline-none"
      >
        <div className="flex items-center gap-3 min-w-0">
          {/* category icon dot */}
          <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${open ? "bg-emerald-500" : "bg-gray-300"}`} />

          <span className="font-semibold text-gray-800 truncate">{name}</span>

          {/* item count chip */}
          <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
            {items.length} item{items.length !== 1 ? "s" : ""}
          </span>

          {/* alert badges */}
          {hasAlert && (
            <div className="hidden sm:flex items-center gap-1.5">
              {outOfStock > 0 && (
                <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">
                  <AlertTriangle size={9} /> {outOfStock} out
                </span>
              )}
              {lowStock > 0 && (
                <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                  <AlertTriangle size={9} /> {lowStock} low
                </span>
              )}
            </div>
          )}
        </div>

        <ChevronDown
          size={17}
          className={`shrink-0 text-gray-400 transition-transform duration-260 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* ── items panel ── */}
      <AccordionPanel open={open} items={items}>
        <div className="bg-white/60">
          {items.map((row, i) => {
            const qty = row.quantity ?? 0;
            const isLow  = qty > 0 && qty <= 10;
            const isZero = qty === 0;
            const needsAttention = isLow || isZero;

            return (
              <div
                key={row._id ?? row.id ?? i}
                className={`flex items-start justify-between gap-4 px-5 sm:px-7 py-3.5 ${
                  i < items.length - 1 ? "border-b border-emerald-100/40" : ""
                } hover:bg-emerald-50/30 transition-colors`}
              >
                {/* left */}
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="font-bold text-gray-900 text-[15px] leading-tight truncate">
                    {row.itemName ?? "—"}
                  </p>

                  {/* Why is it low? — only shown when attention needed */}
                  {needsAttention && (
                    <div className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 w-fit text-xs font-medium ${
                      isZero
                        ? "border-red-200 bg-red-50 text-red-700"
                        : "border-amber-200 bg-amber-50 text-amber-800"
                    }`}>
                      {isZero ? "⚠ No stock remaining" : `⚠ Only ${qty} ${row.unit ?? ""} left`}
                      {row.lastEntryAt && (
                        <span className="text-[10px] font-normal opacity-70">
                          · last updated{" "}
                          {new Date(row.lastEntryAt).toLocaleDateString("en-IN", {
                            day: "2-digit", month: "short",
                          })}
                          {row.lastEntryByName && ` by ${row.lastEntryByName}`}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Normal meta for healthy items */}
                  {!needsAttention && (
                    <p className="text-[11px] text-gray-400 flex flex-wrap gap-x-2">
                      {isChain && row.partner?.name && <span>{row.partner.name}</span>}
                      {row.lastEntryAt && (
                        <span>
                          Updated{" "}
                          {new Date(row.lastEntryAt).toLocaleDateString("en-IN", {
                            day: "2-digit", month: "short",
                          })}
                          {row.lastEntryByName && ` · ${row.lastEntryByName}`}
                        </span>
                      )}
                    </p>
                  )}
                </div>

                {/* right: quantity box */}
                <div className="shrink-0 flex flex-col items-center">
                  <div className={`rounded-xl border-2 px-4 py-2 text-center min-w-[64px] ${
                    isZero
                      ? "border-red-300 bg-red-50"
                      : isLow
                        ? "border-amber-300 bg-amber-50"
                        : "border-emerald-200 bg-emerald-50"
                  }`}>
                    <p className={`text-2xl font-black tabular-nums leading-none ${
                      isZero ? "text-red-600" : isLow ? "text-amber-600" : "text-emerald-700"
                    }`}>
                      {qty}
                    </p>
                    {row.unit && (
                      <p className="text-[10px] text-gray-400 mt-0.5 font-medium">{row.unit}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </AccordionPanel>
    </div>
  );
}

/* ── Main page ──────────────────────────────────────────────────────────── */
export default function InventoryList() {
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch]   = useState(searchParams.get("search") || "");
  const [branchId, setBranchId] = useState(searchParams.get("partner") || "");
  const [branches, setBranches] = useState([]);

  const [allItems, setAllItems] = useState([]);   // full fetched list
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");

  // Which categories are open — default all open so user sees data immediately
  const [openCats, setOpenCats] = useState(new Set());
  const [initialised, setInitialised] = useState(false);

  useEffect(() => {
    if (!isChain) return;
    listBranches({ limit: 100 })
      .then((r) => setBranches(unwrapList(r, 100).items))
      .catch(() => setBranches([]));
  }, [isChain]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const base = {
        limit: API_LIMIT,
        ...(isChain && branchId ? { partner: branchId } : {}),
      };
      // Fetch page 1 first to know total pages, then fetch remaining pages.
      const first = await listStock({ ...base, page: 1 });
      const { items: firstItems, pagination } = unwrapList(first, API_LIMIT);
      const totalPages = pagination.pages ?? 1;

      let all = [...firstItems];
      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            listStock({ ...base, page: i + 2 }).then((r) => unwrapList(r, API_LIMIT).items)
          )
        );
        all = all.concat(rest.flat());
      }

      setAllItems(all);
      setInitialised(true);
    } catch (err) {
      setAllItems([]);
      setError(errorMessage(err, "Could not load stock."));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, isChain]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Reset open state when branch changes
  useEffect(() => { setInitialised(false); }, [branchId]);

  // Sync URL
  useEffect(() => {
    const next = new URLSearchParams();
    if (search) next.set("search", search);
    if (isChain && branchId) next.set("partner", branchId);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, branchId]);

  // Filter by search query
  const filtered = search.trim()
    ? allItems.filter((r) =>
        (r.itemName ?? "").toLowerCase().includes(search.toLowerCase())
      )
    : allItems;

  // Group by category name
  const grouped = filtered.reduce((acc, row) => {
    const cat = row.category?.name ?? "Uncategorised";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(row);
    return acc;
  }, {});
  const categoryNames = Object.keys(grouped).sort();

  // When searching, auto-expand all matching categories
  useEffect(() => {
    if (search.trim()) {
      setOpenCats(new Set(categoryNames));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  function toggleCat(name) {
    setOpenCats((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  }

  function expandAll()   { setOpenCats(new Set(categoryNames)); }
  function collapseAll() { setOpenCats(new Set()); }

  const totalOut = allItems.filter((r) => (r.quantity ?? 0) === 0).length;
  const totalLow = allItems.filter((r) => (r.quantity ?? 0) > 0 && (r.quantity ?? 0) <= 10).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8">
        <PageHeader
          title="Inventory"
          subtitle="Stock grouped by category. Tap a category to see its items."
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Link to="/inventory/entries">
                <button className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50 transition-colors">
                  Movement <ArrowRight size={14} />
                </button>
              </Link>
              <Link to="/inventory/adjust">
                <button className="flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 px-3 py-1.5 text-sm font-medium text-white hover:shadow-lg hover:shadow-emerald-500/25 transition-all">
                  <RefreshCw size={14} /> Adjust
                </button>
              </Link>
            </div>
          }
        />

        {/* Alert strip */}
        {!loading && (totalOut > 0 || totalLow > 0) && (
          <div className="mb-4 flex flex-wrap gap-3">
            {totalOut > 0 && (
              <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5">
                <AlertTriangle size={15} className="text-red-500 shrink-0" />
                <span className="text-sm font-semibold text-red-700">
                  {totalOut} item{totalOut !== 1 ? "s" : ""} out of stock
                </span>
              </div>
            )}
            {totalLow > 0 && (
              <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5">
                <AlertTriangle size={15} className="text-amber-500 shrink-0" />
                <span className="text-sm font-semibold text-amber-700">
                  {totalLow} item{totalLow !== 1 ? "s" : ""} running low
                </span>
              </div>
            )}
          </div>
        )}

        <Card className="bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50 overflow-hidden">
          {/* Search + branch + expand controls */}
          <div className="flex flex-col sm:flex-row gap-3 border-b border-emerald-100/60 p-3 sm:p-4">
            <div className="relative flex-1">
              <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search items…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
              />
            </div>
            {isChain && (
              <div className="flex items-center gap-2">
                <Store size={14} className="shrink-0 text-gray-400" />
                <Select
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  className="sm:w-48 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                >
                  <option value="">All branches</option>
                  {branches.map((b) => (
                    <option key={b._id} value={b._id}>{b.name}</option>
                  ))}
                </Select>
              </div>
            )}
            {/* Expand / collapse all */}
            {!loading && categoryNames.length > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={expandAll}
                  className="rounded-md px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 transition-colors"
                >
                  Expand all
                </button>
                <span className="text-gray-300">·</span>
                <button
                  onClick={collapseAll}
                  className="rounded-md px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 transition-colors"
                >
                  Collapse all
                </button>
              </div>
            )}
          </div>

          {error && (
            <div className="border-b border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="divide-y divide-emerald-100/60">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="px-5 py-4 space-y-2">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-2.5 w-2.5 rounded-full" />
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-5 w-16 rounded-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : categoryNames.length === 0 ? (
            <div className="p-10">
              <EmptyState
                icon={Package}
                title="No stock found"
                description={search ? "No items match your search." : "Stock will appear once employees start recording inventory."}
              />
            </div>
          ) : (
            <div>
              {categoryNames.map((name) => (
                <CategoryRow
                  key={name}
                  name={name}
                  items={grouped[name]}
                  open={openCats.has(name)}
                  onToggle={() => toggleCat(name)}
                  isChain={isChain}
                />
              ))}
            </div>
          )}

          {/* Footer count */}
          {!loading && filtered.length > 0 && (
            <div className="border-t border-emerald-100/60 px-5 py-3 text-xs text-gray-400">
              {filtered.length} item{filtered.length !== 1 ? "s" : ""} across {categoryNames.length} categor{categoryNames.length !== 1 ? "ies" : "y"}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
